import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { hapticLight } from "@/lib/haptics";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        "primary-mobile":
          "bg-ev-500 text-white hover:bg-ev-600 active:bg-ev-700 disabled:bg-ev-100 disabled:text-ev-300",
        "secondary-mobile":
          "bg-sl-100 text-sl-950 hover:bg-sl-200 active:bg-sl-300 disabled:bg-sl-100 disabled:text-sl-300",
        "outline-mobile":
          "border border-sl-500 bg-transparent text-sl-950 shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:border-sl-700 hover:bg-sl-50 active:border-sl-700 active:bg-sl-100 disabled:border-sl-200 disabled:text-sl-300 disabled:shadow-none",
        "ghost-mobile":
          "bg-transparent text-sl-950 hover:bg-sl-100 active:bg-sl-200 disabled:text-sl-300",
        "destructive-mobile":
          "bg-re-200 text-white hover:bg-re-300 active:bg-re-400 disabled:bg-re-50 disabled:text-re-100",
        "subtle-mobile":
          "bg-sl-50 text-sl-700 hover:bg-sl-100 active:bg-sl-200 disabled:bg-sl-50 disabled:text-sl-300",
        "success-mobile":
          "bg-fe-400 text-sl-50 hover:bg-fe-500 active:bg-fe-600 disabled:bg-fe-200 disabled:text-fe-50/80",
        "vibrant-mobile":
          "bg-ev-500 text-white hover:bg-ev-600 active:bg-ev-700 disabled:bg-ev-100 disabled:text-ev-300",
        "icon-mobile":
          "bg-sl-50 text-sl-500 hover:bg-sl-100 active:bg-sl-200 disabled:bg-sl-50 disabled:text-sl-300",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
        "xs-mobile": "min-h-6 px-3 py-1 text-xs font-medium",
        "sm-mobile": "min-h-9 px-4 py-2 text-sm font-semibold",
        "md-mobile": "min-h-10 px-5 py-2.5 text-[0.95rem] font-semibold",
        "lg-mobile":
          "min-h-12 px-5 py-3 text-[0.95rem] font-semibold tracking-tight",
        "xl-mobile":
          "min-h-14 px-6 py-4 text-[1.05rem] font-bold tracking-tight",
        "icon-mobile": "size-10 p-0",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & { asChild?: boolean }
>(
  (
    {
      className,
      variant = "default",
      size = "default",
      asChild = false,
      onClick,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      hapticLight();
      onClick?.(e);
    };

    return (
      <Comp
        ref={ref}
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(buttonVariants({ variant, size, className }))}
        onClick={handleClick}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
